/**
 * Data adapter hooks — pages NEVER import Apollo directly, only these.
 *
 * All list hooks hit the rich paginated queries (PUBLISHED_*_RICH) with
 * `{ options: { paginationOptions: { page, limit }, published: true } }` and
 * normalize to ListState. Detail hooks hit *_BY_ID (after resolveEntityPath
 * for a slug key) and normalize to DetailState. fetchPolicy is
 * cache-and-network (the client default).
 *
 * Note on pagination: the cache type policies replace (not append) the list
 * fields, so pages implement load-more by raising `page` (or accumulating
 * items locally) and `refetch` re-runs the current page.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useQuery, type ApolloError } from '@apollo/client';
import type { DocumentNode } from 'graphql';
import {
  PUBLISHED_BRANDS_RICH,
  PUBLISHED_STORES_RICH,
  PUBLISHED_SHOPS_RICH,
  PUBLISHED_SHOPS_COUNT,
  PUBLISHED_PROMOTIONS_RICH,
  NEARBY_STORES,
  NEARBY_SHOPS,
  RESOLVE_ENTITY_PATH,
} from '@/graphql/queries';
import {
  ENTITY_DETAIL_QUERIES,
  detailVariables,
  resolveVariables,
} from '@/graphql/entityRoutes';
import type { Brand, Store, Shop, Promotion, Pagination } from '@/types';
import type { Locale } from '@/seo/locales';
import { liveFirst } from '@/seo/lifecycle';
import { LIST_PAGE_SIZE, splitLocalePath, type EntityType } from '@/seo/urls';
import { useRequestCountry, useRouteLocale } from '@/hooks/useRouteLocale';
import { usePreloadedQuery } from '@/hooks/routePreload';
import { errorCode } from '@/utils/validation/authErrors';
import { detailStatus, isObjectId, type DetailStatus } from '@/utils/detailStatus';

export interface ListState<T> {
  items: T[];
  loading: boolean;
  error: string | null;
  pagination: Pagination | null;
  refetch: () => void;
}

export interface DetailState<T> {
  item: T | null;
  /**
   * What the page renders (web plan W0.2): `loading` skeleton, `ok` entity,
   * `not_found` noindex + "not found" H1, `error` retry state with robots and
   * canonical unchanged. Classified by `detailStatus` (@/utils/detailStatus).
   */
  status: DetailStatus;
  loading: boolean;
  error: string | null;
  /**
   * `extensions.code` of the failure, kept alongside the message: NOT_FOUND,
   * GONE, '404', RATE_LIMITED, INTERNAL_SERVER_ERROR, … Null without one.
   */
  errorCode: string | null;
  refetch: () => void;
  /**
   * For a key that is not an ObjectId (a slug, an old slug; web plan W0.16):
   * the `canonicalPath` of the backend's 200 or 301 answer, where the page
   * belongs. Null for an ObjectId key, before the answer and for 404 / 410.
   */
  resolvedPath?: string | null;
}

/** `resolveEntityPath`'s answer (backend B1.4 `EntityPathResolution`). */
export interface EntityPathResolution {
  status: number;
  id?: string | null;
  canonicalPath?: string | null;
  robots?: string | null;
  phase?: string | null;
}

/**
 * Options for the filtered hooks. `where` / `sort` are the JSON-stringified
 * Mongo clauses produced by `buildCategoryWhere` / `buildSort` (useCategories).
 */
export interface FilteredOptions {
  page?: number;
  limit?: number;
  where?: string;
  sort?: string;
  /** Send nothing (e.g. a rail whose filter isn't known yet); the state stays empty. */
  skip?: boolean;
}

/** Shown instead of Apollo's raw "Response not successful: Received status code 429". */
export const RATE_LIMITED_MESSAGE = 'Too many requests — please wait a moment and try again.';

/**
 * The `error` string a hook hands its page. The rate-limit middleware's 429
 * reaches Apollo as a bare network error whose message is only the status
 * line; its RATE_LIMITED code lives in the response body, which `errorCode`
 * reads. Every other error keeps its message as before.
 */
export function queryErrorMessage(error: ApolloError | undefined): string | null {
  if (!error) return null;
  if (errorCode(error) === 'RATE_LIMITED') return RATE_LIMITED_MESSAGE;
  return error.message;
}

// ─── Internal generic plumbing ──────────────────────────────────────────────

type RichField =
  | 'publishedBrands'
  | 'publishedStores'
  | 'publishedShops'
  | 'publishedPromotions';

type RichItemsKey = 'brands' | 'stores' | 'shops' | 'promotions';

interface RichPayload<T> {
  pagination?: Pagination | null;
  brands?: T[];
  stores?: T[];
  shops?: T[];
  promotions?: T[];
}

type RichData<T> = Partial<Record<RichField, RichPayload<T> | null>>;

interface RichListArgs {
  page?: number;
  limit?: number;
  where?: string;
  sort?: string;
  skip?: boolean;
}

function useRichList<T>(
  query: DocumentNode,
  field: RichField,
  itemsKey: RichItemsKey,
  { page = 1, limit = LIST_PAGE_SIZE, where, sort, skip = false }: RichListArgs,
): ListState<T> {
  // The route's country once the URL move is live (web plan W2.1); left out before it.
  const country = useRequestCountry();
  const locale = useRouteLocale();
  // A list page's server copy (web plan W4.5, @/hooks/routePreload) answers
  // the page it rendered; every other page loads as before.
  const result = usePreloadedQuery<RichData<T>>(query, {
    variables: {
      options: {
        paginationOptions: { page, limit },
        published: true,
        ...(where ? { where } : {}),
        ...(sort ? { sort } : {}),
        ...(country ? { country } : {}),
      },
    },
    skip,
  });

  const payload = result.data?.[field];
  const rawItems = payload?.[itemsKey];
  // Running promotions before upcoming ones, within the fetched page only. An
  // explicit `sort` is the visitor's choice and keeps the backend's order.
  const items = useMemo(() => {
    const list = rawItems ?? [];
    return itemsKey === 'promotions' && !sort ? liveFirst(list as Promotion[], new Date(), locale) as T[] : list;
  }, [rawItems, itemsKey, sort, locale]);
  const pagination = payload?.pagination ?? null;
  const totalRecords = pagination?.totalRecords ?? 0;

  // NOTE: there used to be a one-shot "widen" here that, once the first page
  // reported the true total, refetched with `limit: totalRecords` — an
  // unbounded request that only worked because the backend had no cap. Its one
  // consumer was the old /nearby path, which now uses the server-side geo
  // query (`useNearbyList`) and accumulates bounded pages instead.


  return {
    items,
    loading: result.loading,
    error: queryErrorMessage(result.error),
    pagination,
    refetch: () => {
      void result.refetch();
    },
  };
}

/** A 200 or a redirect naming an entity: its id and where it belongs. Else null. */
function resolvedEntity(
  answer: EntityPathResolution | null,
): { id: string; path: string | null } | null {
  if (!answer) return null;
  const named = answer.status === 200 || (answer.status >= 300 && answer.status < 400);
  if (!named || !isObjectId(answer.id)) return null;
  return { id: answer.id, path: answer.canonicalPath ?? null };
}

/**
 * A detail page's data for its route key `ref` (web plan W0.2, W0.16):
 *  - an ObjectId → the type's `*ById` query, as before;
 *  - any other key (a slug, an old slug, a mistyped id) → `resolveEntityPath`
 *    first, with the route's locale prefix: 200 or 301 → the `*ById` query
 *    with the id it answers (`resolvedPath` says where the page belongs; the
 *    page moves there, `useEntityPage`); 404 / 410 → `not_found`, as the
 *    backend's NOT_FOUND / GONE codes are; a failed resolution (the retryable
 *    error while country data can't load, 429, 5xx, network) → `error`, never
 *    a 404 — client errors never downgrade robots;
 *  - an empty key → `not_found` without a request.
 */
function useDetailQuery<T>(
  type: EntityType,
  ref: string,
  locale?: Locale | null,
): DetailState<T> {
  const { query, field } = ENTITY_DETAIL_QUERIES[type];
  const byId = isObjectId(ref);
  const resolving = !byId && ref !== '';

  const resolution = useQuery<{ resolveEntityPath: EntityPathResolution | null }>(
    RESOLVE_ENTITY_PATH,
    {
      variables: resolveVariables(type, ref, locale),
      skip: !resolving,
      notifyOnNetworkStatusChange: true,
    },
  );
  const answer = resolving ? (resolution.data?.resolveEntityPath ?? null) : null;
  const found = resolvedEntity(answer);
  const id = byId ? ref : (found?.id ?? null);

  const result = useQuery<Record<string, T | null>>(query, {
    variables: detailVariables(id ?? '', locale),
    skip: !id,
    // Retry after a failed load goes back through `loading` (skeleton) instead
    // of leaving the error on screen until the answer lands.
    notifyOnNetworkStatusChange: true,
  });
  const item = id ? (result.data?.[field] ?? null) : null;
  const loaded = () =>
    detailStatus({ item, loading: result.loading, error: result.error, validParam: true });

  // An id URL without a country prefix (`/store/{id}`) is asked for in the
  // default country; one that is not found there may live in another launched
  // country (a Bahrain store with no slug), which only the resolver can tell
  // (no locale: the entity's own country decides). Asked only then, so the
  // common id URL costs no extra request.
  const unprefixedMiss = byId && !locale && loaded() === 'not_found';
  const placement = useQuery<{ resolveEntityPath: EntityPathResolution | null }>(RESOLVE_ENTITY_PATH, {
    variables: resolveVariables(type, ref, null),
    skip: !unprefixedMiss,
    notifyOnNetworkStatusChange: true,
  });
  const placed = unprefixedMiss ? resolvedEntity(placement.data?.resolveEntityPath ?? null) : null;
  // Answered, and it names a page elsewhere: the page moves there (`resolvedPath`), skeleton meanwhile.
  const moving = placed !== null && placed.path !== null && splitLocalePath(placed.path) !== null;

  let status: DetailStatus;
  let code = errorCode(result.error) ?? null;
  if (byId) {
    status = loaded();
    if (unprefixedMiss && (moving || (!placement.data && !placement.error && placement.loading))) status = 'loading';
  } else if (!resolving) status = 'not_found';
  else if (resolution.error) {
    status = 'error';
    code = errorCode(resolution.error) ?? null;
  } else if (!answer) {
    // No answer and no error: still asking, or a null the contract never
    // sends without an error — a retry state, not a 404.
    status = resolution.loading ? 'loading' : 'error';
  } else if (found) status = loaded();
  else if (answer.status === 404 || answer.status === 410) {
    status = 'not_found';
    code = answer.status === 410 ? 'GONE' : 'NOT_FOUND';
  } else status = 'error';

  const failedResolution = resolving && (Boolean(resolution.error) || !answer);
  return {
    item,
    status,
    loading: result.loading || (resolving && resolution.loading),
    error: queryErrorMessage(resolution.error ?? result.error),
    errorCode: code,
    resolvedPath: found?.path ?? (moving ? placed.path : null),
    refetch: () => {
      // A failed refetch surfaces through the query's `error` (and so
      // `status`); the promise's rejection carries nothing more.
      if (failedResolution) {
        resolution.refetch().catch(() => {});
        return;
      }
      if (!id) return;
      result.refetch().catch(() => {});
    },
  };
}

// ─── Catalog list hooks ─────────────────────────────────────────────────────
//
// `limit` defaults to LIST_PAGE_SIZE, the page size `?page=N` is defined by
// (web plan W0.11); callers showing a short rail pass their own.

export function useBrands(page = 1, limit = LIST_PAGE_SIZE): ListState<Brand> {
  return useRichList<Brand>(PUBLISHED_BRANDS_RICH, 'publishedBrands', 'brands', {
    page,
    limit,
  });
}

export function useStores(page = 1, limit = LIST_PAGE_SIZE): ListState<Store> {
  return useRichList<Store>(PUBLISHED_STORES_RICH, 'publishedStores', 'stores', { page, limit });
}

export function useShops(page = 1, limit = LIST_PAGE_SIZE): ListState<Shop> {
  return useRichList<Shop>(PUBLISHED_SHOPS_RICH, 'publishedShops', 'shops', { page, limit });
}

/** Variables of the published-shop count (exported for tests). */
export const SHOPS_COUNT_VARIABLES = {
  options: { paginationOptions: { page: 1, limit: 1 }, published: true },
} as const;

/**
 * True once the backend reports at least one published shop.
 *
 * The header, drawer and footer link `/shops` only then (web plan W0.3,
 * master D-5: the list is `noindex` and unlinked while it is empty). False
 * while loading and on error, so an empty list is never linked site-wide;
 * the persisted cache answers at once on later visits. The three layout
 * components mount together, so Apollo sends a single request for them.
 */
export function useHasShops(): boolean {
  const country = useRequestCountry();
  const variables = useMemo(
    () =>
      country
        ? { options: { ...SHOPS_COUNT_VARIABLES.options, country } }
        : SHOPS_COUNT_VARIABLES,
    [country],
  );
  const { data } = useQuery<{
    publishedShops?: { pagination?: Pick<Pagination, 'totalRecords'> | null } | null;
  }>(PUBLISHED_SHOPS_COUNT, { variables });
  return (data?.publishedShops?.pagination?.totalRecords ?? 0) > 0;
}

// ─── Nearby (server-side geo search) ────────────────────────────────────────
//
// Ask the backend for the stores/shops near a point (2dsphere $geoNear) instead
// of downloading the whole catalog and haversine-filtering client-side. Results
// come back sorted nearest-first, each carrying `distanceKm`. Skipped until a
// `position` is known. Fetch once at a wide radius; the page narrows to the
// active radius client-side using the returned distanceKm.

interface GeoPosition {
  lat: number;
  lng: number;
}

type NearbyData<T> = {
  nearbyStores?: RichPayload<T> | null;
  nearbyShops?: RichPayload<T> | null;
};

/**
 * Page size for the accumulating geo feeds. Sits under the backend's public
 * pagination cap; the loop below walks pages until it has the whole in-radius
 * set, so the page still gets everything it needs to narrow client-side.
 */
export const NEARBY_PAGE_SIZE = 50;

/**
 * Runaway guard only — NOT a display limit.
 *
 * It was 12 (=600 rows), which real data exceeded: a 50 km search around Lahore
 * returns 773 stores, so 173 were silently dropped. The page now asks the
 * server for the selected radius rather than the widest one, but this ceiling
 * still has to clear a full radius or the same truncation returns quietly.
 */
const NEARBY_MAX_PAGES = 40;

function useNearbyList<T>(
  query: DocumentNode,
  field: 'nearbyStores' | 'nearbyShops',
  itemsKey: 'stores' | 'shops',
  position: GeoPosition | null,
  radiusKm: number,
): ListState<T> {
  // The route's country once the URL move is live (web plan W2.1); left out before it.
  const country = useRequestCountry();
  const countryOption = useMemo(() => (country ? { country } : {}), [country]);
  const baseOptions = useMemo(
    () => ({ paginationOptions: { page: 1, limit: NEARBY_PAGE_SIZE }, published: true, ...countryOption }),
    [countryOption],
  );

  const result = useQuery<NearbyData<T>>(query, {
    variables: {
      lat: position?.lat ?? 0,
      lng: position?.lng ?? 0,
      radiusKm,
      options: baseOptions,
    },
    skip: !position,
    // The page shows a spinner until the whole set has accumulated.
    notifyOnNetworkStatusChange: true,
  });

  const payload = result.data?.[field];
  const items = (payload?.[itemsKey] ?? []) as T[];
  const pagination = payload?.pagination ?? null;
  const totalRecords = pagination?.totalRecords ?? 0;

  const { fetchMore, loading } = result;
  /** Highest page asked for with fetchMore in this walk; 0 = only the query's page 1. */
  const loadedRef = useRef(0);

  // The page after the rows held so far, if every page so far came back full.
  const nextPage = Math.floor(items.length / NEARBY_PAGE_SIZE) + 1;
  // Once `loading` is false every page asked for has answered. A walk of full
  // pages would hold `pagesAsked × NEARBY_PAGE_SIZE` rows, so a `nextPage` at
  // or below the pages already asked for means one came back short (or
  // failed): the server has no more rows to give, whatever `totalRecords`
  // says (web plan W0.19). Asking again would only repeat that page, and
  // waiting for `totalRecords` would keep the spinner on forever.
  const pagesAsked = Math.max(1, loadedRef.current);
  const walkDone =
    !totalRecords ||
    items.length >= totalRecords ||
    nextPage <= pagesAsked ||
    nextPage > NEARBY_MAX_PAGES;

  // Walk the remaining pages. This replaces a single `limit: 500` request,
  // which only ever worked because the backend had no cap — and would silently
  // truncate the moment one was added. The Nearby page needs the full in-radius
  // set (it narrows by the radius pills locally), so we accumulate rather than
  // paginate the UI.
  useEffect(() => {
    if (!position || loading || walkDone) return;
    // Guard against re-entering for the same page if an effect re-runs first.
    if (loadedRef.current >= nextPage) return;
    loadedRef.current = nextPage;

    fetchMore({
      variables: {
        lat: position.lat,
        lng: position.lng,
        radiusKm,
        options: {
          paginationOptions: { page: nextPage, limit: NEARBY_PAGE_SIZE },
          published: true,
          ...countryOption,
        },
      },
      // A failed page ends the walk with the rows already held (see walkDone).
    }).catch(() => {});
  }, [position, radiusKm, loading, walkDone, nextPage, fetchMore, countryOption]);

  // A new fix or radius starts a fresh accumulation.
  useEffect(() => {
    loadedRef.current = 0;
  }, [position?.lat, position?.lng, radiusKm]);

  const stillAccumulating = !walkDone;

  return {
    items,
    loading: loading || stillAccumulating,
    error: queryErrorMessage(result.error),
    pagination,
    refetch: () => {
      loadedRef.current = 0;
      void result.refetch();
    },
  };
}

export function useNearbyStores(position: GeoPosition | null, radiusKm: number): ListState<Store> {
  return useNearbyList<Store>(NEARBY_STORES, 'nearbyStores', 'stores', position, radiusKm);
}

export function useNearbyShops(position: GeoPosition | null, radiusKm: number): ListState<Shop> {
  return useNearbyList<Shop>(NEARBY_SHOPS, 'nearbyShops', 'shops', position, radiusKm);
}

export function usePromotions(page = 1, limit = LIST_PAGE_SIZE): ListState<Promotion> {
  return useRichList<Promotion>(
    PUBLISHED_PROMOTIONS_RICH,
    'publishedPromotions',
    'promotions',
    { page, limit },
  );
}

// ─── Filtered search hooks ──────────────────────────────────────────────────
//
// These mirror useBrands/useShops/useStores/usePromotions but accept the
// JSON-stringified `where` and `sort` clauses produced by `buildCategoryWhere`
// and `buildSort` (see useCategories.ts).

export function useFilteredBrands(options: FilteredOptions = {}): ListState<Brand> {
  return useRichList<Brand>(PUBLISHED_BRANDS_RICH, 'publishedBrands', 'brands', {
    limit: LIST_PAGE_SIZE,
    ...options,
  });
}

export function useFilteredShops(options: FilteredOptions = {}): ListState<Shop> {
  return useRichList<Shop>(PUBLISHED_SHOPS_RICH, 'publishedShops', 'shops', {
    limit: LIST_PAGE_SIZE,
    ...options,
  });
}

export function useFilteredStores(options: FilteredOptions = {}): ListState<Store> {
  return useRichList<Store>(PUBLISHED_STORES_RICH, 'publishedStores', 'stores', {
    limit: LIST_PAGE_SIZE,
    ...options,
  });
}

export function useFilteredPromotions(
  options: FilteredOptions = {},
): ListState<Promotion> {
  return useRichList<Promotion>(
    PUBLISHED_PROMOTIONS_RICH,
    'publishedPromotions',
    'promotions',
    { limit: LIST_PAGE_SIZE, ...options },
  );
}

// ─── Detail hooks ───────────────────────────────────────────────────────────

// `ref` is the route's key, an ObjectId or a slug; `locale` its launched
// `/{cc}/{lang}` prefix, if any (see useDetailQuery).

export function useBrandDetail(ref: string, locale?: Locale | null): DetailState<Brand> {
  return useDetailQuery<Brand>('brand', ref, locale);
}

export function useStoreDetail(ref: string, locale?: Locale | null): DetailState<Store> {
  return useDetailQuery<Store>('store', ref, locale);
}

export function useShopDetail(ref: string, locale?: Locale | null): DetailState<Shop> {
  return useDetailQuery<Shop>('shop', ref, locale);
}

export function usePromotionDetail(ref: string, locale?: Locale | null): DetailState<Promotion> {
  return useDetailQuery<Promotion>('promotion', ref, locale);
}

// ─── Home data hooks ────────────────────────────────────────────────────────

export function useFeaturedPromotions(limit = 6): ListState<Promotion> {
  const state = useRichList<Promotion>(
    PUBLISHED_PROMOTIONS_RICH,
    'publishedPromotions',
    'promotions',
    { page: 1, limit },
  );
  return { ...state, items: state.items.slice(0, limit) };
}

export function usePopularBrands(limit = 10): ListState<Brand> {
  const state = useRichList<Brand>(PUBLISHED_BRANDS_RICH, 'publishedBrands', 'brands', {
    page: 1,
    limit,
  });
  return { ...state, items: state.items.slice(0, limit) };
}

export function useTrendingStores(limit = 10): ListState<Store> {
  const state = useRichList<Store>(PUBLISHED_STORES_RICH, 'publishedStores', 'stores', {
    page: 1,
    limit,
  });
  return { ...state, items: state.items.slice(0, limit) };
}

export function useTrendingShops(limit = 10): ListState<Shop> {
  const state = useRichList<Shop>(PUBLISHED_SHOPS_RICH, 'publishedShops', 'shops', {
    page: 1,
    limit,
  });
  return { ...state, items: state.items.slice(0, limit) };
}
